// PR-P2 — how a driver's salary was built, with one click to edit the model.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserCog, Pencil, FileDown, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient } from '@/lib/api';
import { AddOtherLine } from '@/components/payments/AddOtherLine';
import { openStatementPdf } from '@/lib/statementPdf';

interface PayLine { label: string; amount: number; kind: string }
interface Breakdown {
  driverName: string | null; driverCode: string | null; payModel: string; source: string;
  tripsCount: number; from: string; to: string;
  components: { base: number; perTrip: number; perKm: number; bonus: number };
  gross: number; deductions: { advances: number; reconciledExpenses: number };
  netPayable: number; lines: PayLine[]; explain: string;
}
const MODEL_LABEL: Record<string, string> = {
  fixed: 'Fixed', base_plus_per_trip: 'Base + per trip', base_plus_per_km: 'Base + per km', target_with_bonus: 'Target + bonus',
};
const sar = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

export function DriverDetailSheet({
  driverId, driverName, from, to, label, onClose, onEdit, onReport,
}: {
  driverId: string; driverName: string | null; from: string; to: string; label: string; onClose: () => void; onEdit: () => void; onReport: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['neom-pay-one', driverId, from, to],
    queryFn: async () => (await apiClient.get<Breakdown>(`/api/v1/driver-finance/neom-pay/${driverId}`, { params: { from, to } })).data,
  });
  const changed = () => { qc.invalidateQueries({ queryKey: ['neom-pay'] }); refetch(); };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" /> {driverName ?? 'Driver'} · {label}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-driver-detail>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => openStatementPdf('driver', driverId, from, to).catch((e) => window.alert(String(e)))}>
              <FileDown className="h-4 w-4" /> Statement PDF
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={onReport}>
              <BarChart3 className="h-4 w-4" /> Report
            </Button>
          </div>
          {isLoading || !data ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">{MODEL_LABEL[data.payModel] ?? data.payModel}</span>
                <span className="text-muted-foreground">{data.tripsCount} trips</span>
                {data.source === 'driver_default' && <span className="text-[10px] text-amber-600 dark:text-amber-400">default model</span>}
              </div>

              <div className="rounded-lg border border-border divide-y divide-border">
                {data.lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className={l.amount < 0 ? 'text-rose-600 dark:text-rose-400' : ''}>{l.label}</span>
                    <span className={`font-mono tabular-nums ${l.amount < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}>{l.amount < 0 ? '- ' : ''}{sar(Math.abs(l.amount))}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2 text-sm bg-muted/30">
                  <span className="text-muted-foreground">Gross</span><span className="font-mono tabular-nums">{sar(data.gross)}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2 font-semibold">
                  <span>Net pay</span><span className="font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{sar(data.netPayable)} SAR</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground font-mono">{data.explain}</p>

              <div className="rounded-md border border-border p-2">
                <div className="text-xs text-muted-foreground mb-1">Add a one-off line (bonus, fine, deduction):</div>
                <AddOtherLine subjectKind="driver" subjectId={driverId} onAdded={changed} />
              </div>
            </>
          )}

          <Button onClick={onEdit} className="w-full gap-2"><Pencil className="h-4 w-4" /> Edit pay model</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
